import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { AttachFile } from '@material-ui/icons';
import { FormattedMessage } from 'react-intl';
import { Button } from '../../../common/components';
import { EXCEL_TYPE } from '../../../common/constants';
import messages from '../../messages';
import GuideBox from '../GuideBox';

const text = {
  strong: <FormattedMessage {...messages.excelUploadBoxTextStrong} />,
  helper: <FormattedMessage {...messages.excelUploadBoxTextHelper} />,
};

const S = {
  GuideBox: styled(GuideBox)`
    margin-bottom: 24px;
  `,
  AttachFile: styled(AttachFile)`
    margin-right: 4px;
    color: ${({ theme }) => theme.palette.grey[500]};
    font-size: 1.5rem;
  `,
  FileName: styled.span`
    margin-right: 16px;
    color: ${({ theme }) => theme.palette.grey[900]};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
    font-weight: 500;
    line-height: 1.53;
  `,
  ExcelClearButton: styled.button`
    color: ${({ theme }) => theme.palette.grey[600]};
    font-size: ${({ theme }) => theme.typography.pxToRem(13)};
    font-weight: 500;
    line-height: 1.54;
    cursor: pointer;
    :hover {
      text-decoration: underline;
    }
  `,
  ExcelUploadButton: styled(({ error, ...otherProps }) => (
    <Button {...otherProps} />
  )).attrs({ variant: 'outlined' })`
    height: 40px;
    min-height: 40px;
    padding: 0 16px;
    border-color: ${({ theme }) => theme.palette.primary.main};
    font-size: ${({ theme }) => theme.typography.pxToRem(15)};
  `,
  Input: styled.input`
    display: none;
  `,
};

const ExcelUploadBox = ({ file, setFile, ...otherProps }) => {
  const input = useRef(null);

  const handleClick = useCallback(() => {
    input.current.click();
  }, []);

  const handleChange = useCallback((event) => {
    const [excel] = event.target.files;

    setFile(excel);
  }, [setFile]);

  const handleClear = useCallback(() => setFile(null), [setFile]);

  return (
    <S.GuideBox text={text}>
      {file
        ? (
          <>
            <S.AttachFile />
            <S.FileName>{file.name}</S.FileName>
            <S.ExcelClearButton type="button" onClick={handleClear}>
              <FormattedMessage {...messages.excelUploadBoxExcelClearButton} />
            </S.ExcelClearButton>
          </>
        )
        : (
          <S.ExcelUploadButton
            type="button"
            color="primary"
            variant="outlined"
            onClick={handleClick}
          >
            <S.Input
              type="file"
              accept={EXCEL_TYPE}
              tabIndex="-1"
              ref={input}
              onChange={handleChange}
              {...otherProps}
            />
            <FormattedMessage {...messages.excelUploadBoxExcelUploadButton} />
          </S.ExcelUploadButton>
        )}
    </S.GuideBox>
  );
};

ExcelUploadBox.propTypes = {
  file: PropTypes.instanceOf(File),
  setFile: PropTypes.func.isRequired,
};

ExcelUploadBox.defaultProps = {
  file: null,
};

export default ExcelUploadBox;
